/**
 * Staff and their access.
 *
 * A locked-out cashier at the start of a shift is an emergency, so the reset
 * and unlock actions are on the list itself rather than buried in an edit form.
 *
 * The PIN is what a cashier actually signs in with, so creating a user offers
 * one straight away rather than leaving them to find it in a settings menu.
 */

import { useState, useCallback } from 'react';
import {
    FiPlus, FiUserCheck, FiKey, FiSlash, FiCheck, FiLock, FiEdit2, FiUnlock, FiHash
} from 'react-icons/fi';

import { setupApi } from '../services/endpoints';
import useAsync, { useAction } from '../hooks/useAsync';
import DataTable from '../components/ui/DataTable';
import Modal from '../components/ui/Modal';
import { PageHeader, Badge, StatusBadge } from '../components/ui/Feedback';
import { Field, Input, Checkbox } from '../components/ui/Field';
import { formatDateTime } from '../utils/format';

const EmployeesPage = () => {
    const [creating, setCreating] = useState(null);
    const [editing, setEditing] = useState(null);
    const [resetting, setResetting] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [pinning, setPinning] = useState(null);
    const [newPin, setNewPin] = useState('');

    const load = useCallback(() => setupApi.listUsers(), []);
    const { data: users, loading, refetch } = useAsync(load, []);

    const loadRoles = useCallback(() => setupApi.listRoles(), []);
    const { data: roles } = useAsync(loadRoles, []);

    const { run: create, busy: creatingBusy } = useAction(
        (form) => setupApi.createUser({
            ...form,
            roleIds: [...form.roleIds].map(Number)
        }),
        { successMessage: 'User created.', onSuccess: () => { setCreating(null); refetch(); } }
    );

    const { run: resetPassword, busy: resetting2 } = useAction(
        (userId) => setupApi.resetPassword(userId, newPassword),
        {
            successMessage: 'Password reset.',
            onSuccess: () => { setResetting(null); setNewPassword(''); refetch(); }
        }
    );

    const { run: update, busy: updating } = useAction(
        (form) => setupApi.updateUser(form.User_ID, {
            displayName: form.displayName,
            roleIds: [...form.roleIds].map(Number)
        }),
        { successMessage: 'User updated.', onSuccess: () => { setEditing(null); refetch(); } }
    );

    const { run: setPin, busy: settingPin } = useAction(
        (userId) => setupApi.setUserPin(userId, newPin),
        {
            successMessage: 'PIN set.',
            onSuccess: () => { setPinning(null); setNewPin(''); refetch(); }
        }
    );

    const { run: unlock } = useAction(
        (userId) => setupApi.unlockUser(userId),
        { successMessage: 'Account unlocked.', onSuccess: refetch }
    );

    const { run: setActive } = useAction(
        (userId, isActive) => setupApi.setUserActive(userId, isActive),
        { onSuccess: refetch }
    );

    /**
     * The roles a row currently holds, as the Set of ids the edit form works
     * in. listUsers returns them as a display string, so they are matched back
     * by name against the role list rather than fetched per row.
     */
    const roleIdsOf = (row) => {
        const held = new Set((row.Roles || '').split(', ').filter(Boolean));
        return new Set((roles || []).filter((r) => held.has(r.Role_Name)).map((r) => r.Role_ID));
    };

    const columns = [
        {
            key: 'User_Name',
            header: 'User',
            render: (row) => (
                <div className="min-w-0">
                    <p className="truncate font-medium text-slate-800">
                        {row.Display_Name || row.User_Name}
                    </p>
                    <p className="truncate text-xs text-slate-500">
                        {row.User_Name}
                        {row.Designation_Name && ` · ${row.Designation_Name}`}
                    </p>
                </div>
            )
        },
        {
            key: 'Roles',
            header: 'Roles',
            render: (row) => (
                <div className="flex flex-wrap gap-1">
                    {(row.Roles || '').split(', ').filter(Boolean).map((role) => (
                        <Badge key={role} tone={role === 'Owner' ? 'brand' : 'slate'}>{role}</Badge>
                    ))}
                </div>
            )
        },
        {
            key: 'Has_PIN',
            header: 'Till PIN',
            render: (row) => (row.Has_PIN
                ? <Badge tone="green">set</Badge>
                : <Badge tone="amber">not set</Badge>)
        },
        {
            key: 'Last_Login_Date_Time',
            header: 'Last signed in',
            render: (row) => (
                <span className="text-slate-600">
                    {row.Last_Login_Date_Time ? formatDateTime(row.Last_Login_Date_Time) : 'never'}
                </span>
            )
        },
        {
            key: 'status',
            header: 'Status',
            render: (row) => {
                // A lockout is temporary and self-clearing, so it is worth
                // distinguishing from a disabled account.
                const locked = row.Locked_Until && new Date(row.Locked_Until) > new Date();
                if (!row.Is_Active) return <StatusBadge status="VOID" label="disabled" />;
                if (locked) {
                    return (
                        <span className="flex items-center gap-1 text-xs text-red-600">
                            <FiLock className="h-3.5 w-3.5" />
                            locked until {formatDateTime(row.Locked_Until)}
                        </span>
                    );
                }
                return <StatusBadge status="OK" label="active" />;
            }
        },
        {
            key: 'actions',
            header: '',
            render: (row) => (
                <div className="flex justify-end gap-1">
                    <button
                        type="button"
                        className="btn-ghost btn-sm !px-2"
                        onClick={() => setEditing({
                            ...row,
                            displayName: row.Display_Name || '',
                            roleIds: roleIdsOf(row)
                        })}
                        title="Edit name and roles"
                    >
                        <FiEdit2 className="h-4 w-4" />
                    </button>
                    {/* A lockout clears itself, but not fast enough for a
                        cashier starting a shift with a queue waiting. */}
                    {row.Locked_Until && new Date(row.Locked_Until) > new Date() && (
                        <button
                            type="button"
                            className="btn-ghost btn-sm !px-2 text-amber-600"
                            onClick={() => unlock(row.User_ID)}
                            title="Unlock now"
                        >
                            <FiUnlock className="h-4 w-4" />
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn-ghost btn-sm !px-2"
                        onClick={() => { setPinning(row); setNewPin(''); }}
                        title="Set the till PIN"
                    >
                        <FiHash className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        className="btn-ghost btn-sm !px-2"
                        onClick={() => setResetting(row)}
                        title="Reset the password"
                    >
                        <FiKey className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        className={`btn-ghost btn-sm !px-2 ${row.Is_Active ? 'text-red-600' : 'text-emerald-600'}`}
                        onClick={() => setActive(row.User_ID, !row.Is_Active)}
                        title={row.Is_Active ? 'Disable' : 'Enable'}
                    >
                        {row.Is_Active ? <FiSlash className="h-4 w-4" /> : <FiCheck className="h-4 w-4" />}
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="page">
            <PageHeader
                title="Employees"
                subtitle="Who can sign in, and what they can reach"
                actions={(
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => setCreating({
                            userName: '', displayName: '', password: '', pin: '', roleIds: new Set()
                        })}
                    >
                        <FiPlus className="h-4 w-4" /> New user
                    </button>
                )}
            />

            <DataTable
                className="min-h-0 flex-1"
                columns={columns}
                rows={users || []}
                loading={loading}
                rowKey={(row) => row.User_ID}
                emptyTitle="No users"
                emptyIcon={FiUserCheck}
            />

            {/* New user */}
            <Modal
                open={!!creating}
                onClose={() => setCreating(null)}
                title="New user"
                width="md"
                dismissible={!creatingBusy}
                footer={(
                    <>
                        <button type="button" className="btn-secondary" onClick={() => setCreating(null)}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={() => create(creating)}
                            disabled={
                                creatingBusy
                                || !creating?.userName?.trim()
                                || (creating?.password || '').length < 8
                                || creating?.roleIds?.size === 0
                            }
                        >
                            {creatingBusy ? 'Creating…' : 'Create user'}
                        </button>
                    </>
                )}
            >
                {creating && (
                    <div className="space-y-3">
                        <div className="grid gap-3 sm:grid-cols-2">
                            <Field label="Username" required>
                                <Input
                                    value={creating.userName}
                                    onChange={(e) => setCreating({ ...creating, userName: e.target.value })}
                                    placeholder="ali"
                                    data-autofocus
                                />
                            </Field>
                            <Field label="Display name">
                                <Input
                                    value={creating.displayName}
                                    onChange={(e) => setCreating({ ...creating, displayName: e.target.value })}
                                    placeholder="Ali Raza"
                                />
                            </Field>
                        </div>

                        <Field
                            label="Password"
                            hint="At least 8 characters. They will be asked to change it at first sign-in."
                            required
                        >
                            <Input
                                type="password"
                                value={creating.password}
                                onChange={(e) => setCreating({ ...creating, password: e.target.value })}
                            />
                        </Field>

                        <Field
                            label="Till PIN"
                            hint="4 to 6 digits, for signing in at the till without a keyboard."
                        >
                            <Input
                                inputMode="numeric"
                                maxLength={6}
                                className="tabular"
                                value={creating.pin}
                                onChange={(e) => setCreating({
                                    ...creating,
                                    pin: e.target.value.replace(/\D/g, '')
                                })}
                            />
                        </Field>

                        <Field label="Roles" required>
                            <div className="space-y-1.5">
                                {(roles || []).map((role) => (
                                    <Checkbox
                                        key={role.Role_ID}
                                        label={role.Role_Name}
                                        description={role.Role_Description}
                                        checked={creating.roleIds.has(role.Role_ID)}
                                        onChange={(checked) => {
                                            const next = new Set(creating.roleIds);
                                            if (checked) next.add(role.Role_ID);
                                            else next.delete(role.Role_ID);
                                            setCreating({ ...creating, roleIds: next });
                                        }}
                                    />
                                ))}
                            </div>
                        </Field>
                    </div>
                )}
            </Modal>

            {/* Edit name and roles */}
            <Modal
                open={!!editing}
                onClose={() => setEditing(null)}
                title={`Edit ${editing?.User_Name || ''}`}
                subtitle="Changing roles takes effect the next time they sign in."
                width="md"
                dismissible={!updating}
                footer={(
                    <>
                        <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={() => update(editing)}
                            disabled={updating || editing?.roleIds?.size === 0}
                        >
                            {updating ? 'Saving…' : 'Save changes'}
                        </button>
                    </>
                )}
            >
                {editing && (
                    <div className="space-y-3">
                        <Field label="Display name">
                            <Input
                                value={editing.displayName}
                                onChange={(e) => setEditing({ ...editing, displayName: e.target.value })}
                                placeholder={editing.User_Name}
                                data-autofocus
                            />
                        </Field>

                        <Field label="Roles" required>
                            <div className="space-y-1.5">
                                {(roles || []).map((role) => (
                                    <Checkbox
                                        key={role.Role_ID}
                                        label={role.Role_Name}
                                        description={role.Role_Description}
                                        checked={editing.roleIds.has(role.Role_ID)}
                                        onChange={(checked) => {
                                            const next = new Set(editing.roleIds);
                                            if (checked) next.add(role.Role_ID);
                                            else next.delete(role.Role_ID);
                                            setEditing({ ...editing, roleIds: next });
                                        }}
                                    />
                                ))}
                            </div>
                        </Field>
                    </div>
                )}
            </Modal>

            {/* Set till PIN */}
            <Modal
                open={!!pinning}
                onClose={() => { setPinning(null); setNewPin(''); }}
                title={`Till PIN for ${pinning?.User_Name || ''}`}
                subtitle="What they type at the till when there is no keyboard."
                width="sm"
                dismissible={!settingPin}
                footer={(
                    <>
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => { setPinning(null); setNewPin(''); }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={() => setPin(pinning.User_ID)}
                            disabled={settingPin || !/^\d{4,6}$/.test(newPin)}
                        >
                            {settingPin ? 'Saving…' : 'Set PIN'}
                        </button>
                    </>
                )}
            >
                <Field label="New PIN" hint="4 to 6 digits." required>
                    <Input
                        inputMode="numeric"
                        maxLength={6}
                        className="tabular"
                        value={newPin}
                        onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                        data-autofocus
                    />
                </Field>
            </Modal>

            {/* Reset password */}
            <Modal
                open={!!resetting}
                onClose={() => { setResetting(null); setNewPassword(''); }}
                title={`Reset password for ${resetting?.User_Name || ''}`}
                subtitle="They will be asked to choose a new one at their next sign-in."
                width="sm"
                dismissible={!resetting2}
                footer={(
                    <>
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => { setResetting(null); setNewPassword(''); }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={() => resetPassword(resetting.User_ID)}
                            disabled={resetting2 || newPassword.length < 8}
                        >
                            {resetting2 ? 'Resetting…' : 'Reset password'}
                        </button>
                    </>
                )}
            >
                <Field label="Temporary password" hint="At least 8 characters." required>
                    <Input
                        type="text"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        data-autofocus
                    />
                </Field>
            </Modal>
        </div>
    );
};

export default EmployeesPage;
